import axios from "axios";
import { isCalendarNotConnectedResponseData } from "./calendar-not-connected";

const API_BASE_URL = "/api"; // Now using local Next.js API routes

const MAX_RETRIES = 5;
const RETRY_DELAY = 1000;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const apiRequest = async (
  method: "GET" | "POST" | "PUT" | "DELETE",
  endpoint: string,
  data?: any,
  queryParams?: Record<string, string>,
  retryCount = 0,
  token?: string,
): Promise<any> => {
  if (!token) {
    throw new Error("No authentication token available");
  }

  const url = `${API_BASE_URL}${endpoint}`;

  // Ensure proper URL encoding of query parameters
  let finalUrl = url;
  if (queryParams && Object.keys(queryParams).length > 0) {
    const searchParams = new URLSearchParams();
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value);
      }
    });
    finalUrl = `${url}?${searchParams.toString()}`;
  }

  const config = {
    method,
    url: finalUrl,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    ...(data && { data }),
  };

  try {
    const response = await axios(config);

    return response.data;
  } catch (error: any) {
    // Log error details for debugging - capture all available error information
    if (error.response?.status === 400 || error.response?.status === 422) {
      // Try to parse error response data
      let errorData = error.response?.data;
      let errorMessage = error.message;
      
      // If error.data is a string, try to parse it
      if (typeof errorData === 'string') {
        try {
          errorData = JSON.parse(errorData);
        } catch {
          errorData = errorData; // Keep as string if not JSON
        }
      }
      
      // If errorData is undefined or null, try to get it from error.response
      if (!errorData && error.response) {
        // Try to read the response text if available
        if (error.response.data === undefined || error.response.data === null) {
          errorData = { message: "No error data available in response" };
        } else {
          errorData = error.response.data;
        }
      }
      
      // Extract query params from URL if available
      let parsedQueryParams: Record<string, string> = {};
      try {
        const urlObj = new URL(config.url, 'http://localhost');
        urlObj.searchParams.forEach((value, key) => {
          parsedQueryParams[key] = value;
        });
      } catch {
        // URL parsing failed, skip
      }
      
      // Check if this is a "calendar not connected" error (expected 422)
      const isCalendarNotConnected = isCalendarNotConnectedResponseData(
        error.response?.status,
        errorData,
      );

      // Do not retry 422 CALENDAR_NOT_CONNECTED — throw immediately so callers
      // can return [] and stop repeated calls (see .cursor/rules/CALENDAR_NOT_CONNECTED_422.md).
      if (isCalendarNotConnected) {
        throw error;
      }

      // Only log as error if it's not an expected "calendar not connected" error
      if (!isCalendarNotConnected) {
        // Log comprehensive error details
        const errorLog = {
          method: config.method,
          url: config.url,
          status: error.response?.status,
          statusText: error.response?.statusText,
          errorMessage: errorMessage,
          responseData: errorData,
          responseDataString: typeof errorData === 'object' ? JSON.stringify(errorData, null, 2) : String(errorData),
          responseHeaders: error.response?.headers ? Object.keys(error.response.headers) : [],
          queryParams: parsedQueryParams,
          hasResponse: !!error.response,
          hasData: !!error.response?.data,
          errorType: error.name,
          fullError: error.toString(),
          // Additional debugging info
          errorKeys: errorData && typeof errorData === 'object' ? Object.keys(errorData) : [],
          errorCode: errorData?.code || errorData?.error || null,
          userMessage: errorData?.message || errorData?.userFriendlyMessage || null,
        };
        
        console.error("🔴 [API ERROR] Bad Request/Unprocessable Entity:", errorLog);
      }
    }

    if (error.response?.status === 401 && retryCount < MAX_RETRIES) {
      await sleep(RETRY_DELAY * Math.pow(2, retryCount));

      return apiRequest(
        method,
        endpoint,
        data,
        queryParams,
        retryCount + 1,
        token,
      );
    }

    if (retryCount < MAX_RETRIES) {
      await sleep(RETRY_DELAY * Math.pow(2, retryCount));

      return apiRequest(
        method,
        endpoint,
        data,
        queryParams,
        retryCount + 1,
        token,
      );
    }

    throw error;
  }
};

export const apiGet = (
  endpoint: string,
  token: string,
  queryParams?: Record<string, string>,
) => apiRequest("GET", endpoint, undefined, queryParams, 0, token);

export const apiPost = (endpoint: string, data: any, token: string) =>
  apiRequest("POST", endpoint, data, undefined, 0, token);

export const apiPut = (endpoint: string, data: any, token: string) =>
  apiRequest("PUT", endpoint, data, undefined, 0, token);

export const apiDelete = (endpoint: string, token: string, data?: any) =>
  apiRequest("DELETE", endpoint, data, undefined, 0, token);
